import React, { useEffect, useMemo, useState } from 'react';

// Icons
import { ReactComponent as AttachmentIcon } from '../../../assets/icons/clip.svg';

// Utils
import {
  DEFAULT_OTHER_EXPENSE_ITEMS_PER_PAGE,
  OtherExpenseFilterParams,
  SearchOtherExpenseResponse
} from '../../../redux/api/otherExpenses/otherExpense.model';
import NumberUtils from '../../../utils/numberUtils';
import DateUtils from '../../../utils/dateUtils';

// Components
import TwTable, { TwTableColumn, TwTableData } from '../../../components/TwTable/TwTable';
import TwPaginator from '../../../components/TwPaginator/TwPaginator';
import TwGenericMenu, {
  TwGenericMenuOptions
} from '../../../components/TwGenericMenu/TwGenericMenu';

import './otherExpenseTable.scss';
import { ExpenseUtils } from '../../../utils/expensesUtils';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwDataGrid, { CustomFooterComponent } from '../../../components/TwDataGrid/TwDataGrid';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridSortModel
} from '@mui/x-data-grid-pro';
import { BaseQueryParams } from '../../../redux/Global.model';
import useAuth from '../../../providers/Auth/Auth.provider';
import TwLoadTooltipInfo from '../../../components/TwLoadTooltipInfo/TwLoadTooltipInfo';
import TwTripTooltipContentById from '../../../components/TwLoadTooltipInfo/TwTripTooltipContentById';
import { Trip } from '../../../redux/api/trips/trips.model';
import { Typography } from '@mui/material';
import { useAppDispatch } from '../../../redux/hooks';
import {
  openLoadDetail,
  openTripDetail,
  setEntityOverlayInitialTabId
} from '../../../redux/features/navigation/navigationSlice';
import { updateTripId } from '../../../redux/features/trip/tripSlice';
import TripOverlayExpenses from './TripOverlayExpenses/TripOverlayExpenses';
import { setOrderId, setSelectedTripId } from '../../../redux/features/orders/ordersSlice';
import { USER_ROLE } from '../../../providers/User/User.model';
import { SettlementStatus } from '../../../redux/api/settlement/settlement.model';
import Box from '@mui/material/Box';
import { createDownloadCsv } from '../../../utils/download/downloadUtils';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import {
  useBulkDeleteOtherExpensesMutation,
  useExportOtherExpensesCSVMutation
} from '../../../redux/api/otherExpenses/otherExpenseApi';
import TwWarning from '../../../components/TwWarning/TwWarning';

interface OtherExpenseTableProps {
  otherExpenses: SearchOtherExpenseResponse[];
  totalOtherExpenses: number;
  itemsPerPage?: number;
  currentPage?: number;
  onItemsPerPageChange?: (value: string) => void;
  onPageChange?: (value: number) => void;
  onOpenEditOtherExpenseDialog: (id: string) => void;
  onOpenViewOtherExpenseDialog?: (id: string) => void;
  onOpenAddOtherExpenseDialog?: () => void;
  onOpenDeleteOtherExpenseDialog: (id: string, expenseName: string, equipmentName?: string) => void;
  isTab?: boolean;
  isDriverLink?: boolean;
  pageParams?: BaseQueryParams;
  setPageParams?: (params: BaseQueryParams) => void;
  filterParams?: OtherExpenseFilterParams;
}

const DEFAULT_ITEMS_PER_PAGE = [
  { label: '10', value: '10' },
  { label: '25', value: '25' },
  { label: '50', value: '50' },
  { label: '100', value: '100' }
];

const elementRender = (params: GridRenderCellParams<JSX.Element>): React.ReactNode => {
  const { value } = params;
  return value;
};

export const allColumns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 150,
    sortingOrder: ['desc', 'asc'],
    sortable: true,
    renderCell: elementRender
  },
  {
    field: 'date',
    headerName: 'Date',
    minWidth: 100,
    sortingOrder: ['desc', 'asc'],
    sortable: true,
    renderCell: elementRender
  },
  {
    field: 'type',
    headerName: 'Type',
    minWidth: 100,
    sortingOrder: ['desc', 'asc'],
    sortable: true,
    renderCell: elementRender
  },
  {
    field: 'trip',
    headerName: 'Trip #',
    minWidth: 70,
    sortingOrder: ['desc', 'asc'],
    sortable: true,
    renderCell: elementRender
  },
  {
    field: 'driver',
    headerName: 'Driver',
    minWidth: 70,
    sortingOrder: ['desc', 'asc'],
    sortable: true,
    renderCell: elementRender
  },
  {
    field: 'settlement',
    headerName: 'Settlement',
    minWidth: 70,
    sortingOrder: ['desc', 'asc'],
    sortable: true,
    renderCell: elementRender
  },
  {
    field: 'classification',
    headerName: 'Classification',
    minWidth: 70,
    sortingOrder: ['desc', 'asc'],
    sortable: true,
    renderCell: elementRender
  },
  {
    field: 'equipment',
    headerName: 'Equipment',
    minWidth: 80,
    sortingOrder: ['desc', 'asc'],
    sortable: true,
    renderCell: elementRender
  },
  {
    field: 'amount',
    headerName: 'Amount',
    minWidth: 80,
    sortingOrder: ['desc', 'asc'],
    sortable: true,
    renderCell: elementRender
  },
  {
    field: 'total',
    headerName: 'Total',
    minWidth: 80,
    sortable: false,
    renderCell: elementRender
  },
  {
    field: 'attachments',
    headerName: '',
    minWidth: 30,
    sortable: false,
    resizable: false,
    renderCell: elementRender
  },
  {
    field: 'menu',
    headerName: '',
    width: 50,
    maxWidth: 50,
    sortable: false,
    renderCell: elementRender,
    resizable: false
  }
];

export interface OtherExpenseTableData extends TwTableData {
  name: string | React.ReactNode;
  date: string;
  type: string;
  trip: string | React.ReactNode;
  // frequency: string; Hidden in TW-5469
  driver: string;
  settlement: React.ReactNode;
  classification: string;
  equipment: string;
  amount: string | React.ReactNode;
  total: string;
  attachments: React.ReactNode;
  menu: React.ReactNode;
}

export interface OtherExpenseMenuProps {
  order: SearchOtherExpenseResponse;
  onEdit: (id: string) => void;
  onDelete: (id: string, expenseName: string, equipmentName?: string) => void;
  onView?: (id: string) => void;
  isDriverLink?: boolean;
  isLockedBySettlement: boolean;
}

const OtherExpenseMenu: React.FC<OtherExpenseMenuProps> = ({
  order: { id, name, equipment },
  onEdit,
  onView,
  onDelete,
  isDriverLink = false,
  isLockedBySettlement
}): JSX.Element => {
  const driverLinkOptions: TwGenericMenuOptions[] = [
    {
      label: 'View',
      action: (): void => {
        onView && onView(id);
      }
    }
  ];

  const options: TwGenericMenuOptions[] = [
    {
      label: 'Edit',
      action: (): void => {
        onEdit(id);
      },
      disabled: isLockedBySettlement,
      tooltipText: isLockedBySettlement ? 'This expense is linked to a paid settlement' : ''
    },
    {
      label: 'Delete',
      className: isLockedBySettlement ? '' : 'other_expense_table__delete',
      action: (): void => {
        onDelete(id, name, equipment?.name);
      },
      disabled: isLockedBySettlement,
      tooltipText: isLockedBySettlement ? 'This expense is linked to a paid settlement' : ''
    }
  ];

  return (
    <div className="other_expense_table__options">
      <TwGenericMenu options={isDriverLink ? driverLinkOptions : options} />
    </div>
  );
};

const AttachmentIndicator: React.FC<{ count: number }> = ({ count }): JSX.Element => {
  return (
    <div className="attachment__box">
      <AttachmentIcon />
      {count}
    </div>
  );
};

const OtherExpenseTable: React.FC<OtherExpenseTableProps> = ({
  otherExpenses,
  totalOtherExpenses,
  itemsPerPage = DEFAULT_OTHER_EXPENSE_ITEMS_PER_PAGE,
  currentPage = 1,
  onItemsPerPageChange,
  onPageChange,
  onOpenEditOtherExpenseDialog,
  onOpenViewOtherExpenseDialog,
  onOpenDeleteOtherExpenseDialog,
  isTab = false,
  isDriverLink = false,
  onOpenAddOtherExpenseDialog,
  pageParams = {},
  setPageParams,
  filterParams
}): JSX.Element => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();
  const isAdminOrOwner = userData?.role === USER_ROLE.ADMIN || userData?.role === USER_ROLE.OWNER;

  const [tableData, setTableData] = useState<OtherExpenseTableData[]>([]);
  const isUseRateTableAppSettingEnabled = getTenantSetting('use_rate_table_for_orders');
  const dispatch = useAppDispatch();

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedExpenses, setSelectedExpenses] = useState<SearchOtherExpenseResponse[]>([]);
  const [allExpensesSelected, setAllExpensesSelected] = useState<boolean>(false);
  const [openDeleteWarning, setOpenDeleteWarning] = useState<boolean>(false);
  const pluralizedExpense = useMemo(() => {
    return selectedExpenses.length > 1 ? 'Expenses' : 'Expense';
  }, [selectedExpenses?.length]);

  const [exportOtherExpensesCSV, { isLoading: isExporting }] = useExportOtherExpensesCSVMutation();
  const [bulkDeleteOtherExpenses, { isLoading: isDeleting }] = useBulkDeleteOtherExpensesMutation();

  const enableLtl = userData?.tenant?.ltl_enabled || false;

  const columns = useMemo((): GridColDef[] => {
    const tabColumns = [
      'name',
      'date',
      'driver',
      'type',
      'classification',
      'amount',
      'attachments',
      'menu'
    ];
    if (isTab) {
      return allColumns
        .filter((column) => tabColumns.includes(column.field))
        .map((column) => ({
          ...column,
          sortable: false // Disable sorting for all columns when isTab is true
        }));
    }

    return allColumns;
  }, [isTab]);

  const transformOtherExpenses = (otherExpenses: SearchOtherExpenseResponse[]): void => {
    const getDriverName = (expense: SearchOtherExpenseResponse): string => {
      if (expense.driver) {
        return `${expense.driver.first_name} ${expense.driver.last_name}`;
      }

      return '-';
    };

    const tripReadableIdToShow = (
      trip: Pick<Trip, 'id' | 'readable_id' | 'order_readable_id' | 'sort_order'>
    ): string =>
      !enableLtl && trip?.order_readable_id
        ? `${trip?.order_readable_id}-${trip.sort_order + 1}`
        : trip?.readable_id
        ? `${trip?.readable_id}`
        : '-';

    const onTripClick = (tripId: string, loadId?: string): void => {
      dispatch(setEntityOverlayInitialTabId(tripId));
      if (enableLtl) {
        dispatch(updateTripId(tripId));
        dispatch(openTripDetail());
      } else {
        dispatch(setOrderId(loadId || ''));
        dispatch(setSelectedTripId(tripId));
        dispatch(openLoadDetail());
      }
    };

    const openSettlementInNewTab = (settlementId: string): void => {
      if (!settlementId) return;
      window.open(`/settlements/view/${settlementId}`, '_blank');
    };

    const showSettlementInfo = (
      settlement: {
        id: string;
        status: string;
        period_start: string;
        period_end: string;
        driver_id: string;
        driver_name: string;
      } | null
    ): JSX.Element => {
      let settlementInfo = '';
      if (!settlement || settlement.status !== SettlementStatus.PAID) {
        return <Typography component={'span'}>-</Typography>;
      }

      if (settlement.period_start && settlement.period_end) {
        settlementInfo = DateUtils.formatPeriodToMonthDay(
          `${settlement.period_start} - ${settlement.period_end}`,
          false
        );
      }

      if (settlement.driver_name) {
        settlementInfo += ` • ${settlement.driver_name}`;
      }

      return (
        <Typography
          component={'span'}
          sx={{ color: '#3A4DE9', fontSize: '14px', cursor: 'pointer' }}
          onClick={(): void => {
            if (isAdminOrOwner && settlement.status === SettlementStatus.PAID) {
              openSettlementInNewTab(settlement.id);
            } else {
              return;
            }
          }}
        >
          {settlementInfo}
        </Typography>
      );
    };

    const transformedOtherExpenses: OtherExpenseTableData[] = otherExpenses.map(
      (expense: SearchOtherExpenseResponse) => {
        return {
          id: expense.id,
          name: expense.name,
          date: `${DateUtils.getRangeMidiumDate(
            new Date(expense.date_from),
            expense.date_to ? new Date(expense.date_to) : null
          )}`,
          type: `${expense.type ? ExpenseUtils.getTypeLabel(expense.type) : ''}`,
          trip:
            !isTab && expense.trip ? (
              <TwLoadTooltipInfo
                tooltipContent={
                  <TwTripTooltipContentById
                    tripId={expense.trip.id}
                    orderInfo={{
                      orderReadableId: expense.trip.order_readable_id || '',
                      tripsLength: 1,
                      tags: []
                    }}
                  />
                }
              >
                <Typography
                  id={expense.trip.id}
                  onClick={(): void => onTripClick(expense.trip!.id, expense.trip!.load_id)}
                  component={'span'}
                  sx={{ color: '#3A4DE9', fontSize: '14px', cursor: 'pointer' }}
                >{`${expense.trip ? tripReadableIdToShow(expense.trip) : ''}`}</Typography>
              </TwLoadTooltipInfo>
            ) : (
              <></>
            ),
          // Hidden in TW-5469
          // frequency: StringUtils.capitalizeFirstLetter(
          //   expense.frequency.replace('_', '-').toLowerCase()
          // ),
          driver: getDriverName(expense),
          settlement: showSettlementInfo(expense.settlement),
          classification: `${
            expense.classification
              ? ExpenseUtils.getClassificationLabel(expense.classification)
              : ''
          }`,
          equipment: expense.equipment?.name || '-',
          amount: `$${NumberUtils.formatNumber(expense.amount, 4, 2)}`,
          total: `$${NumberUtils.formatNumber(expense.amount, 4, 2)}`,
          attachments:
            expense.other_expense_attachments.length > 0 ? (
              <>
                <AttachmentIndicator count={expense.other_expense_attachments.length} />
              </>
            ) : (
              ''
            ),
          menu: (
            <OtherExpenseMenu
              order={expense}
              onEdit={onOpenEditOtherExpenseDialog}
              onDelete={onOpenDeleteOtherExpenseDialog}
              onView={onOpenViewOtherExpenseDialog}
              isDriverLink={isDriverLink}
              isLockedBySettlement={expense.is_locked_by_settlement}
            />
          )
        };
      }
    );

    // add total row when is tab
    if (isTab) {
      transformedOtherExpenses.push({
        id: 'tabTotal',
        name: (
          <TwButton
            key="1"
            type={TwButtonType.text}
            label="+ Add expense"
            sx={{
              width: 'max-content',
              textTransform: 'none !important'
            }}
            action={(): void => {
              onOpenAddOtherExpenseDialog && onOpenAddOtherExpenseDialog();
            }}
          />
        ),
        date: '',
        type: '',
        trip: '',
        driver: '',
        classification: '',
        settlement: '',
        equipment: '',
        amount: (
          <b>{`$${NumberUtils.formatNumber(
            otherExpenses.reduce((acc, curr) => acc + Number(curr.amount), 0),
            0
          )}`}</b>
        ),
        total: '',
        attachments: '',
        menu: ''
      });
    }

    setTableData(transformedOtherExpenses);
  };

  useEffect(() => {
    otherExpenses && transformOtherExpenses(otherExpenses);
  }, [otherExpenses]);

  const clearSelectionState = (): void => {
    setSelectionModel([]);
    setSelectedExpenses([]);
    setAllExpensesSelected(false);
  };

  const handleSelectAll = (): void => {
    const expenseIds = otherExpenses.map((expense) => expense.id);

    setSelectionModel(expenseIds);
    setSelectedExpenses(otherExpenses);
    setAllExpensesSelected(true);
  };

  const handleSelectionModelChange = (newSelectionModel: GridRowSelectionModel): void => {
    // Turn off flag with any selection change
    setAllExpensesSelected(false);

    if (newSelectionModel.length === 0) {
      // no checkboxes selected
      setSelectedExpenses([]);
    } else if (selectionModel.length > newSelectionModel.length) {
      // selection removed
      const removedIds = selectionModel.filter((id) => !newSelectionModel.includes(id));

      setSelectedExpenses((prev) =>
        prev.filter((workOrder) => removedIds.indexOf(workOrder.id) === -1)
      );
    } else {
      // selection added
      const newExpenseIds = newSelectionModel.filter((id) => !selectionModel.includes(id));

      const newExpenses = otherExpenses.filter((expense) => newExpenseIds.includes(expense.id));
      if (newExpenses && newExpenses.length > 0)
        setSelectedExpenses((prev) => [...prev, ...newExpenses]);
    }

    setSelectionModel(newSelectionModel);
  };

  const selectionCount = allExpensesSelected ? totalOtherExpenses : selectedExpenses.length;

  const handleSortModelChange = (sortModel: GridSortModel): void => {
    let sort_by = '';

    if (sortModel.length > 0) {
      switch (sortModel[0].field) {
        case 'name':
          sort_by = 'name';
          break;
        case 'date':
          sort_by = 'dateFrom';
          break;
        case 'type':
          sort_by = 'type';
          break;
        case 'trip':
          sort_by = 'tripNumber';
          break;
        case 'driver':
          sort_by = 'driverName';
          break;
        case 'classification':
          sort_by = 'classification';
          break;
        case 'equipment':
          sort_by = 'equipmentName';
          break;
        case 'amount':
          sort_by = 'amount';
          break;
        default:
          sort_by = sortModel[0].field;
          break;
      }

      if (setPageParams) {
        setPageParams({
          ...pageParams,
          sort_by: sort_by,
          order_by: sortModel[0].sort || undefined
        });
      }
    }
  };

  const generateFilterBody = (): OtherExpenseFilterParams => {
    return {
      ...(!allExpensesSelected && selectedExpenses.length > 0
        ? { otherExpenseIds: selectedExpenses.map((expense) => expense.id) }
        : {}),
      ...(allExpensesSelected ? filterParams : {})
    };
  };

  const exportCsv = (): void => {
    exportOtherExpensesCSV(generateFilterBody())
      .unwrap()
      .then((csv) => {
        addNotification({
          message: 'CSV has been exported successfully',
          variant: 'success'
        });
        createDownloadCsv(csv, 'other-expenses');
      })
      .catch((error) => {
        logRocketTrack('Other Expense', {
          action: 'Export other expenses',
          severity: 'medium',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: error.data?.message
            ? `Error: ${error.data.message}`
            : 'Error, could not export Other expenses.',
          variant: 'error'
        });
      });
  };

  const bulkDelete = (): void => {
    bulkDeleteOtherExpenses(generateFilterBody())
      .unwrap()
      .then(() => {
        clearSelectionState();
        addNotification({
          message: `${pluralizedExpense} deleted successfully`,
          variant: 'success'
        });
      })
      .catch((error) => {
        logRocketTrack('Other Expense', {
          action: 'Delete other expenses',
          severity: 'medium',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: error.data?.message
            ? `Error: ${error.data.message}`
            : 'Error, could not delete Other expenses.',
          variant: 'error'
        });
      });
  };

  return (
    <>
      <TwDataGrid
        className="expenses__data-grid"
        entityName="expense"
        rows={tableData}
        columns={columns}
        checkboxSelection={true}
        disableRowSelectionOnClick
        pagination
        paginationModel={{ page: currentPage - 1, pageSize: itemsPerPage }}
        paginationMode="server"
        rowCount={totalOtherExpenses}
        sortingMode={isTab ? undefined : 'server'}
        hideFooter
        hideFooterPagination
        showSelectAllInFooter
        keepNonExistentRowsSelected
        onSortModelChange={isTab ? undefined : handleSortModelChange}
        pinnedColumns={{
          left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, allColumns[0].field],
          right: ['menu']
        }}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={handleSelectionModelChange}
      />
      {!isTab && (
        <TwPaginator
          currentPage={currentPage as number}
          items={totalOtherExpenses}
          itemsPerPage={itemsPerPage as number}
          onPageChange={onPageChange as (value: number) => void}
          itemsPerPageOptions={DEFAULT_ITEMS_PER_PAGE}
          selectItemsPerPage={onItemsPerPageChange}
          sxContainer={{
            position: 'relative',
            marginBottom: '30px'
          }}
        />
      )}

      {!isTab && (
        <TripOverlayExpenses
          enableLtl={enableLtl}
          isUseRateTableAppSettingEnabled={isUseRateTableAppSettingEnabled}
          userData={userData}
        />
      )}

      {selectionModel.length > 0 && (
        <Box className="all-loads__floating-footer">
          <CustomFooterComponent
            entityName="Expense"
            footerClassName="all-loads__floating-footer-container"
            rowSelectionModel={selectionModel}
            onCancel={clearSelectionState}
            actions={[
              <Box key="menu options" component="div" className="invoices-footer-actions">
                <TwButton
                  key="export"
                  fullwidth={false}
                  type={TwButtonType.secondary}
                  label={`Export ${selectionCount} ${pluralizedExpense}`}
                  action={exportCsv}
                  disabled={isExporting}
                />
                <TwGenericMenu
                  key="menu"
                  className="other_expense_footer__menu"
                  options={[
                    {
                      label: `Delete ${selectionCount} ${pluralizedExpense}`,
                      action: (): void => setOpenDeleteWarning(true),
                      className: 'other_expense_footer__delete'
                    }
                  ]}
                />
              </Box>
            ]}
            showSelectAllInFooter={true}
            onSelectAllFooter={handleSelectAll}
            selectedCountOverride={
              allExpensesSelected ? totalOtherExpenses : selectedExpenses.length
            }
          />
        </Box>
      )}
      <TwWarning
        warningTitle={`Delete ${pluralizedExpense}?`}
        warningMessage={
          <div>
            {`Are you sure you want to delete ${selectionCount} ${pluralizedExpense}? You will be unable to undo this action.`}
          </div>
        }
        open={openDeleteWarning}
        handleClose={(): void => setOpenDeleteWarning(false)}
        showWarning={openDeleteWarning}
        setShowWarning={setOpenDeleteWarning}
        onContinue={bulkDelete}
        customizedContinue={{ label: 'Delete', type: TwButtonType.negative }}
        disabled={isDeleting}
      />
    </>
  );
};

export default OtherExpenseTable;
